import React, { useEffect, useState } from 'react';

import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import Swal from 'sweetalert2';
import Select from 'react-select';

import {
  ATTRIBUTE_SCORES_GROUP_BY_SECTION,
  ATTRIBUTES_SECTIONS,
  ATTRIBUTE_SUB_SECTION_SCORES_UPDATE,
  ATTRIBUTE_SUB_SECTION_SCORES_GROUP_BY_SECTION,
} from 'lib/networking/endpoints';
import AttributeSubCategoryScoringRow from 'components/layout/attribute_scoring/AttributeSubCategoryScoringRow';
import Container from 'components/shared/Container';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import {
  l0BeverageCategoryId,
  l0FoodCategoryId,
  l0OperationalsCategoryId,
} from 'lib/enums/TaxonomyLevel';
import SectionLevelScoringView from 'components/layout/attribute_scoring/SectionLevelScoringView';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

// Sections that use section-level scoring — a single aggregate score replaces
// the per-subsection breakdown.
const SECTION_LEVEL_SECTIONS = new Set(['Claims', 'Top-Off']);

function AttributeSubCategoryScoringView() {
  const [records, setRecords] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    setRecords([]);
    setIsEditing(false);
  }, [selectedCategory]);

  const { postData: createRecords } = usePost(
    ATTRIBUTE_SUB_SECTION_SCORES_UPDATE,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'created successfully',
      });
      setIsEditing(false);
    },
    error =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `An error occurred: ${error?.response?.data?.message}`,
      }),
  );

  const { data: { data: sections = [] } = [], sectionsLoading } = useGet(ATTRIBUTES_SECTIONS);

  const { data: { attribute_scores = [] } = {} } = useGet(ATTRIBUTE_SCORES_GROUP_BY_SECTION, {});

  const { data: attributesCountBySection, loading: attributesCountBySectionLoading } = useGet(
    ATTRIBUTE_SUB_SECTION_SCORES_GROUP_BY_SECTION,
    { attribute_section_id: selectedCategory?.value },
    !selectedCategory,
  );

  useEffect(() => {
    if (attributesCountBySection) {
      const tempRecords = Object.keys(attributesCountBySection).map(subSectionName => ({
        subCategoryName: subSectionName,
        subSectionId: attributesCountBySection[subSectionName].sub_section_id,
        attributesCount: attributesCountBySection[subSectionName].total_attributes,
        FdScore: attributesCountBySection[subSectionName]?.[l0FoodCategoryId?.toString()] || {},
        BevScore:
          attributesCountBySection[subSectionName]?.[l0BeverageCategoryId?.toString()] || {},
        OpsScore:
          attributesCountBySection[subSectionName]?.[l0OperationalsCategoryId?.toString()] || {},
        foodTotalAttributes: attributesCountBySection[subSectionName].fd_total_attributes || 0,
        bevTotalAttributes: attributesCountBySection[subSectionName].bev_total_attributes || 0,
        opsTotalAttributes: attributesCountBySection[subSectionName].ops_total_attributes || 0,
      }));
      setRecords(tempRecords);
    }
  }, [attributesCountBySection]);

  const handleUpdate = (subCategoryName, scoreType, value) => {
    const updatedRecords = records.map(rec => {
      if (rec.subCategoryName === subCategoryName) {
        rec[scoreType] = { ...rec[scoreType], user_input_total_attributes_count: value };
      }
      return rec;
    });
    setRecords(updatedRecords);
  };

  const calculateTotals = () => {
    const baseTotals = records.reduce(
      (acc, rec) => ({
        foodTotal: acc.foodTotal + rec.foodTotalAttributes,
        bevTotal: acc.bevTotal + rec.bevTotalAttributes,
        opsTotal: acc.opsTotal + rec.opsTotalAttributes,
        foodUserTotalAttributes:
          acc.foodUserTotalAttributes + Number(rec.FdScore.user_input_total_attributes_count || 0),
        bevUserTotalAttributes:
          acc.bevUserTotalAttributes + Number(rec.BevScore.user_input_total_attributes_count || 0),
        opsUserTotalAttributes:
          acc.opsUserTotalAttributes + Number(rec.OpsScore.user_input_total_attributes_count || 0),
        foodSubGroupMaxScore: 0,
        bevSubGroupMaxScore: 0,
        opsSubGroupMaxScore: 0,
      }),
      {
        foodTotal: 0,
        bevTotal: 0,
        opsTotal: 0,
        foodUserTotalAttributes: 0,
        bevUserTotalAttributes: 0,
        opsUserTotalAttributes: 0,
        foodSubGroupMaxScore: 0,
        bevSubGroupMaxScore: 0,
        opsSubGroupMaxScore: 0,
      },
    );

    if (selectedCategory) {
      records.forEach(rec => {
        const calculatedScores = calculateScoresForRecord(rec, baseTotals, selectedCategory.label);
        baseTotals.foodSubGroupMaxScore += calculatedScores.fd_max_score;
        baseTotals.bevSubGroupMaxScore += calculatedScores.bev_max_score;
        baseTotals.opsSubGroupMaxScore += calculatedScores.ops_max_score;
      });
    }

    return baseTotals;
  };

  function updateAttributesSubSectionScores() {
    const data = records.flatMap(rec => [
      {
        l0_category_id: l0FoodCategoryId,
        attribute_sub_section_id: rec.subSectionId,
        user_input_total_attributes_count: rec.FdScore.user_input_total_attributes_count,
      },
      {
        l0_category_id: l0BeverageCategoryId,
        attribute_sub_section_id: rec.subSectionId,
        user_input_total_attributes_count: rec.BevScore.user_input_total_attributes_count,
      },
      {
        l0_category_id: l0OperationalsCategoryId,
        attribute_sub_section_id: rec.subSectionId,
        user_input_total_attributes_count: rec.OpsScore.user_input_total_attributes_count,
      },
    ]);
    createRecords({ data: data });
  }

  const tableHeaders = (
    <thead>
      <tr>
        <th className="text-center">Category</th>
        <th className="text-center">Sub Category</th>
        <th className="text-center">Total Attributes</th>
        <th className="text-center">Total Food Attributes (System)</th>
        <th className="text-center">Total Food Attributes (User)</th>
        <th></th>
        <th className="text-center">Total Beverage Attributes (System)</th>
        <th className="text-center">Total Beverage Attributes (User)</th>
        <th></th>
        <th className="text-center">Total Operations Attributes (System)</th>
        <th className="text-center">Total Operations Attributes (User)</th>
        <th></th>
      </tr>
    </thead>
  );

  const tableFooter = () => {
    const totals = calculateTotals();
    return (
      <tfoot>
        <tr className="font-weight-bold">
          <td className="text-center">Total</td>
          <td></td>
          <td></td>
          <td className="text-center">{totals.foodTotal}</td>
          <td className="text-center">{totals.foodUserTotalAttributes}</td>
          <td className="text-center">{Number(totals.foodSubGroupMaxScore.toFixed(2))}</td>
          <td className="text-center">{totals.bevTotal}</td>
          <td className="text-center">{totals.bevUserTotalAttributes}</td>
          <td className="text-center">{Number(totals.bevSubGroupMaxScore.toFixed(2))}</td>
          <td className="text-center">{totals.opsTotal}</td>
          <td className="text-center">{totals.opsUserTotalAttributes}</td>
          <td className="text-center">{Number(totals.opsSubGroupMaxScore.toFixed(2))}</td>
        </tr>
      </tfoot>
    );
  };

  const loading = attributesCountBySectionLoading || sectionsLoading;

  // section score * (attr count for the sub section / total attr count for the section)
  const calculateMaxScore = (l0CategoryId, totalAttributes, totalForCategory, categoryName) => {
    const sectionScore =
      attribute_scores.filter(
        rec => rec.attribute_section === categoryName && rec.l0_category_id === l0CategoryId,
      )[0]?.max_score || 0;

    return sectionScore * (totalAttributes / totalForCategory);
  };

  const calculateScoresForRecord = (record, totals, categoryName) => {
    return {
      fd_max_score: calculateMaxScore(
        l0FoodCategoryId,
        record.FdScore.user_input_total_attributes_count,
        totals.foodUserTotalAttributes,
        categoryName,
      ),
      bev_max_score: calculateMaxScore(
        l0BeverageCategoryId,
        record.BevScore.user_input_total_attributes_count,
        totals.bevUserTotalAttributes,
        categoryName,
      ),
      ops_max_score: calculateMaxScore(
        l0OperationalsCategoryId,
        record.OpsScore.user_input_total_attributes_count,
        totals.opsUserTotalAttributes,
        categoryName,
      ),
    };
  };

  const isSectionLevel = selectedCategory && SECTION_LEVEL_SECTIONS.has(selectedCategory.label);

  return (
    <Container className={classNames(css(styles.container), 'mx-3')}>
      <Row>
        <Col>
          <h3 className="font-weight-bold">Manage Sub Category Scores</h3>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Category</Form.Label>
            <Select
              value={selectedCategory}
              options={sections.map(section => ({
                label: section.attribute_section_name,
                value: section.id,
              }))}
              onChange={e => setSelectedCategory(e)}
              isSearchable
              isClearable
            />
          </Form.Group>
        </Col>
        {!isSectionLevel && (
          <Col className="text-right mt-3">
            <Button
              disabled={!selectedCategory}
              variant={isEditing ? 'danger' : 'primary'}
              onClick={
                isEditing ? () => updateAttributesSubSectionScores() : () => setIsEditing(true)
              }
            >
              {isEditing ? 'Update Values' : 'Edit Values'}
            </Button>
          </Col>
        )}
      </Row>
      {isSectionLevel ? (
        loading ? (
          <LoadingSpinner />
        ) : (
          attributesCountBySection && (
            <SectionLevelScoringView
              selectedCategory={selectedCategory}
              sectionSubData={attributesCountBySection}
            />
          )
        )
      ) : (
        <Table hover className="mt-3">
          {tableHeaders}
          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              <tbody>
                {attributesCountBySection &&
                  selectedCategory &&
                  records.map((rec, index) => {
                    const totals = calculateTotals();
                    const calculatedScores = calculateScoresForRecord(
                      rec,
                      totals,
                      selectedCategory.label,
                    );

                    return (
                      <AttributeSubCategoryScoringRow
                        key={index}
                        categoryName={selectedCategory.label}
                        data={rec}
                        isEditing={isEditing}
                        onUpdate={handleUpdate}
                        calculatedScores={calculatedScores}
                      />
                    );
                  })}
              </tbody>
              {records.length > 0 && tableFooter()}
            </>
          )}
        </Table>
      )}
    </Container>
  );
}

export const styles = StyleSheet.create({
  container: {
    minHeight: 'calc(100vh - 150px)',
  },
});

export default AttributeSubCategoryScoringView;
